import { useState } from 'react';
import { PencilIcon, TrashIcon } from 'lucide-react';

import { Vehicle } from '../../types/vehicle.type';
import BasicButton from '../../components/basic-button/basic-button.component';
import { ApiPermissions } from '../../constants/api-permissions';
import { usePermissions } from '../../hooks/usePermissions';
import { generateUUID } from '../../utils/uuid.util';

import EditVehicleModal from './edit-vehicle-modal';
import DeleteVehicleModal from './delete-vehicle-modal';
import styles from './vehicles.module.scss';

interface Props {
    vehicles?: Vehicle[];
    onAdd: () => void;
    onUpdate: () => void;
}

const Vehicles = ({ vehicles, onAdd, onUpdate }: Props) => {
    const { hasPermission } = usePermissions();
    const canCreateVehicles = hasPermission(ApiPermissions.CREATE_VEHICLES);
    const canUpdateVehicles = hasPermission(ApiPermissions.UPDATE_VEHICLES);
    const canDeleteVehicles = hasPermission(ApiPermissions.DELETE_VEHICLES);

    const [vehicleToEdit, setVehicleToEdit] = useState<Vehicle | null>(null);
    const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(
        null
    );

    const onEditDone = () => {
        setVehicleToEdit(null);
        onUpdate();
    };

    const onDeleteDone = () => {
        setVehicleToDelete(null);
        onUpdate();
    };

    const renderVehiclesList = (vehicles: Vehicle[]) => {
        return (
            <ul>
                {vehicles.map((vehicle) => (
                    <li key={generateUUID()}>
                        <span>
                            {vehicle.brand} - {vehicle.model}
                            <i>|</i>
                            Color: {vehicle.color}
                        </span>
                        <span>
                            Placa: {vehicle.plate}
                            <i>|</i>
                            Año:{' '}
                            {vehicle.manufactureYear ?? <i>No registrado</i>}
                            <i>|</i>
                            Seguro: {vehicle.insurance ?? <i>No posee</i>}
                            {vehicle.manufactureYear == null && (
                                <span
                                    className={styles.incompleteBadge}
                                    title="Falta el año de fabricación"
                                >
                                    Falta año de fabricación
                                </span>
                            )}
                            {vehicle.insurance != null && (
                                <span
                                    className={styles.insuranceBadge}
                                    title="El vehículo tiene un seguro activo"
                                >
                                    Seguro activo
                                </span>
                            )}
                            {canUpdateVehicles && (
                                <BasicButton
                                    style={{ height: 22, fontSize: 12 }}
                                    onClick={() => setVehicleToEdit(vehicle)}
                                >
                                    <PencilIcon size={16} />
                                </BasicButton>
                            )}
                            {canDeleteVehicles && (
                                <BasicButton
                                    style={{ height: 22, fontSize: 12 }}
                                    onClick={() => setVehicleToDelete(vehicle)}
                                >
                                    <TrashIcon size={16} />
                                </BasicButton>
                            )}
                        </span>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <section className={styles.wrapper}>
            <h3>
                Vehículos
                <BasicButton
                    style={{ height: 28 }}
                    onClick={onAdd}
                    disabled={!canCreateVehicles}
                    disabledReason={
                        !canCreateVehicles
                            ? 'No tiene permisos para añadir vehículos'
                            : undefined
                    }
                >
                    + Añadir vehículo
                </BasicButton>
            </h3>
            {!vehicles || vehicles.length === 0 ? (
                <p className={styles.noVehicles}>
                    El usuario no tiene vehículos agregados
                </p>
            ) : (
                renderVehiclesList(vehicles)
            )}
            {vehicleToEdit && (
                <EditVehicleModal vehicle={vehicleToEdit} onDone={onEditDone} />
            )}
            {vehicleToDelete && (
                <DeleteVehicleModal
                    vehicle={vehicleToDelete}
                    onDone={onDeleteDone}
                />
            )}
        </section>
    );
};

export default Vehicles;
